import { v4 } from "uuid";
import { call, take, select, put } from "../../lib";
import { doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT } from "./identity_refresh_token_submit";
import {
  AUTH_REFRESH_UNAVAILABLE,
  AUTH_REFRESHING_FAILURE,
  AUTH_SESSION_UNVERIFIED_CLEAR,
  AUTH_SET,
  LOGOUT,
} from "../state/auth";

// Exactly one of these follows every refresh attempt, so a caller that waits on all three
// always hears back. Waiting on AUTH_SET and AUTH_REFRESHING_FAILURE alone is what used to hold
// every request saga for the whole length of an outage.
export const REFRESH_OUTCOME_ACTION_LIST = [
  AUTH_SET,
  AUTH_REFRESHING_FAILURE,
  AUTH_REFRESH_UNAVAILABLE,
];

// What can end a wait on an unverified session: the tracker accepting a fresh answer, or the session
// ending or being replaced. A refresh's own AUTH_SET is among them only so the flag is read again;
// the reducer leaves it set. AUTH_REFRESH_UNAVAILABLE is deliberately absent: an unanswered check is
// no answer, and this is the one wait that outlasts an outage.
const SESSION_VERIFIED_ACTION_LIST = [
  AUTH_SESSION_UNVERIFIED_CLEAR,
  AUTH_SET,
  AUTH_REFRESHING_FAILURE,
  LOGOUT,
];

// if the token expires within this window, refresh before handing it out
const TOKEN_REFRESH_WINDOW_MS = 15000;

function* waitForRefreshOutcome() {
  // one outcome ends the wait: a transient one means the token is unchanged and a retry is
  // scheduled, which is the caller's cue to go ahead with what it has
  let isRefreshing: boolean = yield select((state) => state?.auth?.isRefreshing);
  while (isRefreshing) {
    yield take(REFRESH_OUTCOME_ACTION_LIST);
    isRefreshing = yield select((state) => state?.auth?.isRefreshing);
  }
}

// In this order: an attempt in flight is waited out; otherwise a scheduled retry means the
// current token is handed back at once, so user activity can neither defeat the schedule nor be
// blocked by it; otherwise a token that is about to expire is refreshed the usual way.
function* waitForTokenRefresh() {
  const isRefreshing: boolean = yield select((state) => state?.auth?.isRefreshing);
  if (isRefreshing) {
    yield call(waitForRefreshOutcome);
    return;
  }

  const refreshUnavailable: boolean = yield select((state) => state?.auth?.refreshUnavailable);
  if (refreshUnavailable) return;

  const accessTokenExpireTimestamp: number =
    (yield select((state) => state?.auth?.accessTokenExpireTimestamp)) ?? 0;
  const expireTime = accessTokenExpireTimestamp - new Date().getTime();
  if (expireTime >= TOKEN_REFRESH_WINDOW_MS) return;

  const requestId = v4();
  yield put(doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT({ requestId }));

  // the outcome, not AUTH_REFRESHING: under `takeLeading` a dispatch that lands on a running
  // cycle is dropped and no AUTH_REFRESHING follows it
  yield take(REFRESH_OUTCOME_ACTION_LIST);
}

type AuthOf = { auth?: { accessToken?: string | null; sessionUnverified?: boolean } };
const sessionUnverifiedOf = (state: AuthOf): boolean => Boolean(state?.auth?.sessionUnverified);
const accessTokenOf = (state: AuthOf): string | null => state?.auth?.accessToken ?? null;

function* waitWhileSessionUnverified() {
  let unverified: boolean = yield select(sessionUnverifiedOf);
  while (unverified) {
    yield take(SESSION_VERIFIED_ACTION_LIST);
    unverified = yield select(sessionUnverifiedOf);
  }
}

// Saga to get token, waits refreshing finished. Past an idle end the server has not confirmed, no
// request goes out on the token and none starts a refresh of its own: the caller waits for the
// tracker's verdict, then takes the usual path, which may still refresh an expiring token.
export function* getToken() {
  let unverified: boolean = yield select(sessionUnverifiedOf);
  for (;;) {
    if (unverified) {
      yield call(waitWhileSessionUnverified);
      // the session ended while it was unverified: nothing to hand out and nothing to refresh
      const remaining: string | null = yield select(accessTokenOf);
      if (!remaining) return null;
    }
    yield call(waitForTokenRefresh);
    // the flag may have been set while this caller waited on a refresh
    unverified = yield select(sessionUnverifiedOf);
    if (!unverified) break;
  }
  const token: string | null = yield select(accessTokenOf);
  return token;
}
